import { Result } from '@doda/common/src/interfaces/api/recommendPanel';

/**
 * Shuffle login recommendations after marking top 4
 *
 * Shuffles the recommendation list while distributing marked items (isHighlyRecommended)
 * to avoid clustering all marked items together. Ensures marked items are spread out
 * with at most 1-3 consecutive marked items for better visual distribution.
 *
 * @param results - Array of job recommendations (may include isHighlyRecommended flag)
 * @returns Shuffled array with marked items distributed and isHighlyRecommended flags preserved
 */
export const shuffleLoginRecommends = (results: Result[]): Result[] => {
  const { highlighted, normal } = results.reduce<{
    highlighted: Result[];
    normal: Result[];
  }>(
    (acc, item) => {
      if (item.isHighlyRecommended) {
        acc.highlighted.push(item);
      } else {
        acc.normal.push(item);
      }
      return acc;
    },
    { highlighted: [], normal: [] }
  );

  if (highlighted.length === 0 || normal.length === 0) {
    return results.slice();
  }

  const caseIndex = (Date.now() % 3) + 1;

  const MAX_CONSECUTIVE_HIGHLIGHTED_MAP: Record<number, number> = {
    1: 1,
    2: 2,
    3: 3,
  };

  const MAX_CONSECUTIVE_HIGHLIGHTED =
    MAX_CONSECUTIVE_HIGHLIGHTED_MAP[caseIndex] ?? 1;

  const output: Result[] = [];

  let hIndex = 0;
  let nIndex = 0;

  while (hIndex < highlighted.length || nIndex < normal.length) {
    let placedHighlighted = 0;

    while (
      hIndex < highlighted.length &&
      placedHighlighted < MAX_CONSECUTIVE_HIGHLIGHTED
    ) {
      output.push(highlighted[hIndex]);
      hIndex += 1;
      placedHighlighted += 1;
    }

    if (nIndex < normal.length) {
      output.push(normal[nIndex]);
      nIndex += 1;
    }
  }

  return output;
};
